import { useState } from 'react'

import type { DashboardWidget } from '../../api/types'
import { t } from '../../i18n/lingua'
import { Finestra } from '../ModificheGitlab'
import type { DefinizioneWidget } from './catalogo'
import { SceltaBersaglio } from './SceltaBersaglio'
import { SceltaImmagine } from './SceltaImmagine'
import { SceltaCIParametro } from './SceltaCIParametro'
import { LINK_VUOTO, SceltaLink, type LinkScelto } from './SceltaLink'
import { SceltaSerie, type SerieScelta } from './SceltaSerie'
import { SceltaVista } from './SceltaVista'

/**
 * The editor of one widget, in a window: the title the person gives it
 * (empty = the catalogue's), then the widget's own options — a menu, a
 * switch, a text, the «CI and parameter» choice, the list of series. Nothing
 * is written until «Salva»: the dashboard behind keeps showing the widget as
 * it was.
 */
export function EditorWidget({ widget, definizione, onSalva, onChiudi }: {
  widget: DashboardWidget
  definizione: DefinizioneWidget
  onSalva: (options: Record<string, unknown>) => void
  onChiudi: () => void
}) {
  const [opzioni, setOpzioni] = useState<Record<string, unknown>>({ ...(widget.options ?? {}) })
  const imposta = (chiave: string, valore: unknown) => setOpzioni((o) => ({ ...o, [chiave]: valore }))
  const titolo = typeof opzioni.title === 'string' ? opzioni.title : ''

  return (
    <Finestra titolo={t('Modifica il widget «{w}»', { w: definizione.label() })} onChiudi={onChiudi}>
      <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); onSalva(opzioni) }}>
        <h4 className="form__sezione">{t('Titolo')}</h4>
        <div className="form-grid">
          <label className="field span-6">
            <span className="field__label">{t('Titolo del widget')}</span>
            <input type="text" value={titolo} onChange={(e) => imposta('title', e.target.value)} maxLength={80}
                   placeholder={definizione.label()} />
            <span className="field__hint">{t('Lascia vuoto per il titolo del catalogo; quello del catalogo resta come sottotitolo.')}</span>
          </label>
        </div>

        {(definizione.opzioni ?? []).length > 0 && (
          <>
            <h4 className="form__sezione">{t('Opzioni')}</h4>
            <div className="form-grid">
              {(definizione.opzioni ?? []).map((o) => {
                if (o.dinamica === 'ci_param') {
                  return (
                    <SceltaCIParametro key={o.key} ciId={String(opzioni.ci_id ?? '')} chiave={String(opzioni.key ?? '')}
                                       onCambia={(ci_id, key, ci_name) => setOpzioni((v) => ({ ...v, ci_id, key, ci_name }))} />
                  )
                }
                if (o.dinamica === 'immagine') {
                  return <SceltaImmagine key={o.key} src={String(opzioni[o.key] ?? '')} onCambia={(src) => imposta(o.key, src)} />
                }
                if (o.tipo === 'multilinea') {
                  return (
                    <label key={o.key} className="field span-6">
                      <span className="field__label">{o.label()}</span>
                      <textarea rows={4} value={String(opzioni[o.key] ?? o.predefinito)} onChange={(e) => imposta(o.key, e.target.value)} maxLength={2000} />
                      {o.aiuto && <span className="field__hint">{o.aiuto()}</span>}
                    </label>
                  )
                }
                if (o.dinamica === 'bersaglio') {
                  return (
                    <SceltaBersaglio key={o.key} address={String(opzioni.address ?? '')} ciId={String(opzioni.ci_id ?? '')}
                                     ciName={String(opzioni.ci_name ?? '')} probeName={String(opzioni.probe_name ?? '')}
                                     onCambia={(b) => setOpzioni((v) => ({ ...v, ...b }))} />
                  )
                }
                if (o.dinamica === 'vista_topologia') {
                  return (
                    <SceltaVista key={o.key} viewId={String(opzioni[o.key] ?? '')}
                                 onCambia={(id, nome) => setOpzioni((v) => ({ ...v, [o.key]: id, view_name: nome }))} />
                  )
                }
                if (o.dinamica === 'link') {
                  const v = opzioni[o.key]
                  const link: LinkScelto = v && typeof v === 'object' ? { ...LINK_VUOTO, ...(v as Partial<LinkScelto>) } : LINK_VUOTO
                  return (
                    <div key={o.key} className="field span-6">
                      <span className="field__label">{o.label()}</span>
                      <SceltaLink link={link} onCambia={(l) => imposta(o.key, l)} />
                      {o.aiuto && <span className="field__hint">{o.aiuto()}</span>}
                    </div>
                  )
                }
                if (o.dinamica === 'serie') {
                  const serie = Array.isArray(opzioni[o.key]) ? (opzioni[o.key] as SerieScelta[]) : []
                  return (
                    <div key={o.key} className="field span-6">
                      <span className="field__label">{o.label()}</span>
                      <SceltaSerie serie={serie} onCambia={(s) => imposta(o.key, s)} soloTipi={o.soloTipi} max={o.massimo} />
                      {o.aiuto && <span className="field__hint">{o.aiuto()}</span>}
                    </div>
                  )
                }
                if (o.tipo === 'bool') {
                  const acceso = opzioni[o.key] == null ? o.predefinito === 'true' : opzioni[o.key] === true || opzioni[o.key] === 'true'
                  return (
                    <label key={o.key} className="checkbox span-3">
                      <input type="checkbox" checked={acceso} onChange={(e) => imposta(o.key, e.target.checked)} />
                      <span>{o.label()}{o.aiuto && <small>{o.aiuto()}</small>}</span>
                    </label>
                  )
                }
                if (o.tipo === 'testo') {
                  return (
                    <label key={o.key} className="field span-3">
                      <span className="field__label">{o.label()}</span>
                      <input type="text" value={String(opzioni[o.key] ?? o.predefinito)} onChange={(e) => imposta(o.key, e.target.value)} maxLength={120} />
                      {o.aiuto && <span className="field__hint">{o.aiuto()}</span>}
                    </label>
                  )
                }
                return (
                  <label key={o.key} className="field span-3">
                    <span className="field__label">{o.label()}</span>
                    <select value={String(opzioni[o.key] ?? o.predefinito)} onChange={(e) => imposta(o.key, e.target.value)}>
                      {o.valori.map((v) => <option key={v.value} value={v.value}>{v.label()}</option>)}
                    </select>
                    {o.aiuto && <span className="field__hint">{o.aiuto()}</span>}
                  </label>
                )
              })}
            </div>
          </>
        )}

        <div className="form__azioni" style={{ marginTop: 16 }}>
          <button type="submit">{t('Salva')}</button>
          <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
        </div>
      </form>
    </Finestra>
  )
}
